import React, { useState, useEffect, useRef } from 'react';
import api from './api';
import './Doctors.css';
import CustomDataTable from './CustomDataTable';
import { FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
//import { useLocation } from 'react-router-dom';
import Cal from './Cal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useAuth from '../hooks/useAuth';

// Build default timePref: next 3 upcoming weekdays (Mon-Fri) from today, AM slot
const getDefaultTimePref = () => {
  const today = new Date().getDay(); // 0=Sun, 1=Mon, ... 6=Sat
  const weekdays = [1, 2, 3, 4, 5]; // Mon-Fri
  // Get weekdays that are today or later this week, then wrap to next week
  const upcoming = [];
  for (let offset = 0; offset < 7 && upcoming.length < 3; offset++) {
    const day = (today + offset) % 7;
    if (weekdays.includes(day)) upcoming.push(day);
  }
  const pref = {};
  upcoming.forEach(d => { pref[d] = { AM: true }; });
  return pref;
};

const PostConsultation = ({ patientCard, patientFData }) => {
  const { auth } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [suggestedServices, setSuggestedServices] = useState([]);
  const [suggestedServicesCopy, setSuggestedServicesCopy] = useState([]);
  const [todayDate] = useState(new Date());
  const [therapists, setTherapists] = useState([]);
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const modSeqs = useRef([]);
  const [appointments, setAppointments] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedRowsState, setSelectedRowsState] = useState([]);
const selectedPatientRef = useRef(null);
const [timePref, setTimePref] = useState({});
const [previouslyBoughtServices, setPreviouslyBoughtServices] = useState([]);
const [billedExpanded, setBilledExpanded] = useState(false);


  useEffect(() => {
  if (!patientCard) return;

  Promise.all([
    api.get(`${process.env.REACT_APP_URL}/patients/profileconsultation/${patientCard}`),
    api.get(`${process.env.REACT_APP_URL}/patients/profiles/${patientCard}`),
  ]).then(([consultRes, profileRes]) => {
      const patient = consultRes.data;
      setSelectedPatient(patient);
      selectedPatientRef.current = patient;
      setTimePref(patient.timePref || {});
      setRecommendations(patient.recommendations || []);

      // Build all GTP services
      const allGtp = (Array.isArray(patient.planServices) && patient.planServices.length > 0)
        ? patient.planServices
        : (patient.suggestedServices || []);

      // Build previously billed from bills
      const bills = profileRes.data?.bills || profileRes.data?._doc?.bills || [];
      const prevBilled = [];
      bills.forEach(b => {
        (b.services || []).forEach(s => {
          prevBilled.push({
            name: s.name || '',
            qty: s.qty || s.quantity || 1,
            amount: s.amount || s.rate || 0,
            _billing: { invoicedate: b.invoicedate, paid: b.paid, balance: b.balance, total: b.total },
          });
        });
      });

      // Count how many times each service name appears in bills
      const billedNameCounts = {};
      prevBilled.forEach(s => { billedNameCounts[s.name] = (billedNameCounts[s.name] || 0) + 1; });

      // Remove GTP services that are already billed (match by name, respecting duplicates)
      const usedCounts = {};
      const unbilledGtp = [];
      allGtp.forEach(s => {
        const n = s.name || '';
        usedCounts[n] = (usedCounts[n] || 0) + 1;
        if (usedCounts[n] <= (billedNameCounts[n] || 0)) {
          // This occurrence is already billed — skip from current GTP
        } else {
          unbilledGtp.push(s);
        }
      });

      // Separate active (qty > 0) from exhausted (qty 0) services
      const activeGtp = unbilledGtp.filter(s => (Number(s.qty) || 0) > 0);
      const exhaustedGtp = unbilledGtp.filter(s => (Number(s.qty) || 0) <= 0);
      setSuggestedServices(activeGtp);
      setSuggestedServicesCopy(activeGtp);
      // Move exhausted (qty 0) services into previously billed table
      const exhaustedAsBilled = exhaustedGtp.map(s => ({
        name: s.name || '', qty: 0, amount: s.amount || 0,
        _billing: { invoicedate: null, paid: true, balance: 0 },
      }));
      setPreviouslyBoughtServices([...prevBilled, ...exhaustedAsBilled]);

      // Preselect all active GTP services
      setSelectedRowsState(activeGtp);

      getTherapists();
      getTherapistSuggestions('startDate', patient.startDate || todayDate, patient);
    })
    .catch((err) => console.error('Error fetching consultation data', err));
}, [patientCard]);

  const getTherapists = () => {
    api.get(`${process.env.REACT_APP_URL}/users/therapist`)
      .then((res) => setTherapists(res.data))
      .catch((err) => console.error('Error fetching therapists:', err));
  };

  const getTherapistSuggestions = (key, value, inputPatient = null) => {
    const updated = { ...selectedPatient, ...(inputPatient || {}), [key]: value };
    setSelectedPatient(updated);
    selectedPatientRef.current = updated; // ✅ Keep ref in sync

    // Build time slots only if startDate is present
  if (!updated.startDate) return;
    const service = updated?.suggestedServices?.[0] || {};
    const sequences = service.sequences || [];

    const totalMin = sequences.reduce((sum, s) => sum + ((s.duration || 0) * (s.repetition || 1)), 0);
    const durationInDays = service.qty || 1;
    const requiredMinPerDay = Math.ceil(totalMin / durationInDays);
    const slotDuration = 60;
    //const daysArray = mapDaysPerWeekToArray(updated.daysperweek || 'MTWRF');
     // Build daysAWeek from new format
  const daysArray = Object.entries(updated.timePref || {})
    .filter(([_, val]) => val.AM || val.PM)
    .map(([day]) => parseInt(day, 10));

    const slotsPerDay = Math.ceil(requiredMinPerDay / slotDuration);

    const patternInput = {
      startDate: new Date(updated.startDate || todayDate),
      durationInDays,
      daysAWeek: daysArray,
      slotDurationInMinutes: slotDuration,
      workingHours: { start: 6, end: 18 }
    };

    const appointmentDates = generateAppointmentDates(patternInput);
    console.log('appointment dates = ', appointmentDates);
  };

  const mapDaysPerWeekToArray = (str = '') => {
    const mapping = { M: 1, T: 2, W: 3, R: 4, F: 5, S: 6, U: 0 };
    return str.split('').map(ch => mapping[ch]).filter(Boolean);
  };

  const generateAppointmentDates = ({ startDate, durationInDays, daysAWeek, slotDurationInMinutes, workingHours }) => {
    const dates = [];
    for (let i = 0; i < durationInDays; i++) {
      const current = new Date(startDate);
      current.setDate(current.getDate() + i);
      if (daysAWeek.includes(current.getDay())) {
        const dateStr = current.toISOString().split('T')[0];
        const slots = generateTimeSlotsForDay(dateStr, workingHours.start, workingHours.end, slotDurationInMinutes);
        dates.push({ date: dateStr, timeSlots: slots });
      }
    }
    return { dates };
  };

  const generateTimeSlotsForDay = (dateStr, startHour, endHour, slotDuration) => {
    const slots = [];
    let current = new Date(`${dateStr}T${String(startHour).padStart(2, '0')}:00`);
    const end = new Date(`${dateStr}T${String(endHour).padStart(2, '0')}:00`);
    while (current < end) {
      const slotEnd = new Date(current.getTime() + slotDuration * 60 * 1000);
      if (slotEnd > end) break;
      slots.push({ start: new Date(current), end: slotEnd });
      current = slotEnd;
    }
    return slots;
  };
const updateServicesToBeSent = () => {
  const final = [];

  for (let i = 0; i < selectedRowsState.length; i++) {
    const originalService = selectedRowsState[i];
    const modifiedService = modSeqs.current.find(m => m._id === originalService._id);

    const updated = { ...originalService };

    const isPkg = /package/i.test(originalService.name || '') && Number(originalService._masterQty || 0) > 1;
    if (modifiedService && !isPkg) {
      const originalQty = parseInt(originalService.qty || 0, 10);
      const modifiedQty = parseInt(modifiedService.modifiedQtyByPatient || originalQty, 10);

      // ❌ Skip if qty is zero or negative
      if (modifiedQty <= 0) continue;

      updated.qty = modifiedQty;
      updated.modifiedQtyByPatient = modifiedQty - originalQty;
      updated.remarks = `${modifiedService.remarks || ''}${modifiedQty !== originalQty ? ` | Qty adjusted to ${modifiedQty}` : ''}`;
    }

    // Skip services with zero or no qty
    if (!updated.qty || updated.qty <= 0) continue;

    //updated.sstartDate = selectedPatient?.startDate;
    updated.startDate = selectedPatientRef.current?.startDate;

    final.push(updated);
  }

  return final;
};

  const updateTherapist = () => {
    if (!selectedPatient?.therapist || selectedPatient.therapist === 'selectT') {
      alert('Please assign a therapist');
      return;
    }
    api.post(`${process.env.REACT_APP_URL}/patients/profiles/therapist/${selectedPatient.therapist}/patient/${selectedPatient.patientCard}`)
      .then((resp) => {
        alert("Therapist updated successfully");
      })
      .catch((err) => alert(err))
  }

  const updateSeqs = async () => {

    const services = updateServicesToBeSent();

    const mergedPatientData = {
      ...selectedPatientRef.current,
      ...patientFData,
      startDate: selectedPatientRef.current.startDate instanceof Date
        ? selectedPatientRef.current.startDate
        : new Date(selectedPatientRef.current.startDate),
      therapist: selectedPatientRef.current.therapist
        ? selectedPatientRef.current.therapist
        : "veena",
      timePref: selectedPatientRef.current.timePref
        ? selectedPatientRef.current.timePref
        : {}
    };

    // Default time preference: Mon(1), Wed(3), Fri(5) Morning if not set
    if (!mergedPatientData.timePref || Object.keys(mergedPatientData.timePref).length === 0) {
      mergedPatientData.timePref = getDefaultTimePref();
    }

    if (!mergedPatientData.therapist || mergedPatientData.therapist === 'selectT') {
      alert('Please assign a therapist');
      return;
    }
    if (!mergedPatientData.suggestedServices || mergedPatientData.suggestedServices.length == 0) {
      alert('Please add services during Consultation');
      return;
    }

    // Save therapist assignment at BE before navigating to Billing
    try {
      await api.post(`${process.env.REACT_APP_URL}/patients/profiles/therapist/${mergedPatientData.therapist}/patient/${patientCard}`);
    } catch (err) {
      alert('Failed to update therapist: ' + (err?.response?.data?.message || err.message));
      return;
    }

    navigate('/Billing', {
      state: {
        statedata: {
          patientCard,
          services,
          patientCData: mergedPatientData
        }
      }
    });
  };

  if (!patientCard) {
    return <div>Loading or invalid patient...</div>;
  }

  const tableData = {
    columns: [
      { name: "Recommendation", selector: row => row?.recommendation, sortable: true },
      { name: "Quantity", selector: row => row?.qty, sortable: true },
      { name: "No. of Days", selector: row => row?.days, sortable: true },
      { name: "No. Of Time", selector: row => row?.not, sortable: true },
      { name: "StartDate", selector: row => row?.startDate },
      { name: "Remarks", selector: row => row?.remarks }
    ],
    data: recommendations
  };

  const _isPkg = (svc) => /package/i.test(svc.name || '') && Number(svc._masterQty || 0) > 1;

  const sstableData = {
    columns: [
      { name: "Name", selector: row => row?.name, sortable: true },
      { name: "Description", selector: row => row?.description, sortable: true },
      { name: "Amount", selector: row => row?.amount, sortable: true },
      { name: "Qty", selector: row => row?.qty, sortable: true },
      {
        name: "Modified Qty",
        cell: (row, idx) => _isPkg(row)
          ? <span style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>Package (fixed)</span>
          : (
          <input
            type="number"
            min="0"
            value={row.modifiedQtyByPatient || ''}
            onChange={e => {
              const updated = suggestedServices.map(item =>
                item._id === row._id ? { ...item, modifiedQtyByPatient: e.target.value } : item
              );
              setSuggestedServices(updated);

              modSeqs.current = [...modSeqs.current.filter(r => r._id !== row._id), updated.find(i => i._id === row._id)];

              if (!selectedRowsState.some(r => r._id === row._id)) {
                setSelectedRowsState([...selectedRowsState, updated.find(i => i._id === row._id)]);
              }
            }}
          />
        )
      },
     {
        name: "Remarks",
        cell: (row, idx) => (
          <input
            type="text"
            value={row.remarks || ''}
            onChange={e => {
              const updated = suggestedServices.map(item =>
                item._id === row._id ? { ...item, remarks: e.target.value } : item
              );
              setSuggestedServices(updated);

              modSeqs.current = [...modSeqs.current.filter(r => r._id !== row._id), updated.find(i => i._id === row._id)];

              if (!selectedRowsState.some(r => r._id === row._id)) {
                setSelectedRowsState([...selectedRowsState, updated.find(i => i._id === row._id)]);
              }
            }}
          />
        )
      }

    ],
    data: suggestedServices
  };

  // Shared card container style
  const card = {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
    marginBottom: 16,
    overflow: 'hidden',
  };

  const cardHeader = (icon, title) => (
    <div style={{
      padding: '12px 18px',
      borderBottom: '1px solid #f3f4f6',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>{title}</span>
    </div>
  );

  return (
    <div style={{ padding: '4px 0' }}>

      {/* ── Start Date ── */}
      <div style={card}>
        {cardHeader('📅', 'Start Date')}
        <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <DatePicker
            selected={selectedPatient?.startDate instanceof Date ? selectedPatient.startDate : todayDate}
            onChange={(date) => getTherapistSuggestions('startDate', date)}
          />
        </div>
      </div>

      {/* ── Time Preferences ── */}
      <div style={card}>
        {cardHeader('🕐', 'Time Preferences (Day-wise)')}
        <div style={{ padding: '12px 18px', overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Day</th>
                <th style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Morning</th>
                <th style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Evening</th>
              </tr>
            </thead>
            <tbody>
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((dayName, dayIdx) => (
                <tr key={dayIdx} style={{ borderBottom: '1px solid #f3f4f6', background: timePref?.[dayIdx]?.AM || timePref?.[dayIdx]?.PM ? '#eff6ff' : '#fff' }}>
                  <td style={{ padding: '7px 14px', fontWeight: timePref?.[dayIdx]?.AM || timePref?.[dayIdx]?.PM ? 600 : 400, color: '#374151' }}>{dayName}</td>
                  <td style={{ padding: '7px 14px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={timePref?.[dayIdx]?.AM || false}
                      style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                      onChange={(e) => {
                        const updated = { ...timePref };
  if (!updated[dayIdx]) updated[dayIdx] = {};
  if (e.target.checked) {
    updated[dayIdx].AM = true;
  } else {
    delete updated[dayIdx].AM;
  }
  if (!updated[dayIdx].AM && !updated[dayIdx].PM) {
    delete updated[dayIdx];
  }
                        setTimePref(updated);
                        const newPatient = { ...selectedPatientRef.current, timePref: updated };
                        setSelectedPatient(newPatient);
                        selectedPatientRef.current = newPatient;
                        getTherapistSuggestions('timePref', updated, newPatient);
                      }}
                    />
                  </td>
                  <td style={{ padding: '7px 14px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={timePref?.[dayIdx]?.PM || false}
                      style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                      onChange={(e) => {
                        const updated = { ...timePref };
                        if (!updated[dayIdx]) updated[dayIdx] = {};
  if (e.target.checked) {
    updated[dayIdx].PM = true;
  } else {
    delete updated[dayIdx].PM;
  }
  if (!updated[dayIdx].AM && !updated[dayIdx].PM) {
    delete updated[dayIdx];
  }
                        setTimePref(updated);
                        const newPatient = { ...selectedPatientRef.current, timePref: updated };
                        setSelectedPatient(newPatient);
                        selectedPatientRef.current = newPatient;
                        getTherapistSuggestions('timePref', updated, newPatient);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Therapist ── */}
      <div style={card}>
        {cardHeader('👤', 'Assign Therapist')}
        <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <select
            value={selectedPatient?.therapist || 'selectT'}
            onChange={(e) => setSelectedPatient({ ...selectedPatient, therapist: e.target.value })}
            style={{
              padding: '9px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: 8,
              fontSize: 13,
              color: '#1f2937',
              background: '#fafafa',
              minWidth: 180,
              cursor: 'pointer',
            }}
          >
            <option value="selectT">Select Therapist</option>
            {therapists.map(t => (
              <option key={t.username} value={t.username}>{t.username}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowCalendar(true)}
            title="View Calendar"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 14px',
              border: '1.5px solid #e5e7eb',
              borderRadius: 8,
              background: '#f9fafb',
              color: '#6b7280',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <FaCalendarAlt style={{ fontSize: 14 }} />
            Calendar
          </button>
          <button
            type="button"
            onClick={updateTherapist}
            style={{
              padding: '9px 20px',
              border: '1.5px solid #2563eb',
              borderRadius: 8,
              background: '#eff6ff',
              color: '#1d4ed8',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Update Therapist
          </button>
        </div>
      </div>

      {showCalendar && (
        <div className="calendar-overlay">
          <div className="calendar-modal">
            <button className="close-btn" onClick={() => setShowCalendar(false)}>×</button>
            <Cal />
          </div>
        </div>
      )}

      {/* ── Recommendations ── */}
      <div style={card}>
        {cardHeader('📋', 'Recommendations')}
        <div style={{ padding: '12px 18px' }}>
          {recommendations.length > 0 ? (
            <CustomDataTable
              columns={tableData.columns}
              data={tableData.data}
              paginationPerPage={10}
              selectableRows={false}
            />
          ) : (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '12px 0' }}>No recommendations recorded.</div>
          )}
        </div>
      </div>

      {/* ── Current GTP Services (for billing) ── */}
      <div style={card}>
        {cardHeader('🛠️', 'Current GTP — Services for Billing')}
        <div style={{ padding: '12px 18px' }}>
          {suggestedServices.length > 0 ? (
            <CustomDataTable
              columns={sstableData.columns}
              data={sstableData.data}
              paginationPerPage={10}
              selectableRows={true}
             onSelectedRowsChange={(e) => {
                const selected = e.selectedRows;
                const updatedMods = modSeqs.current.filter(m => selected.some(s => s._id === m._id));
                const merged = selected.map(row => {
                  const existing = updatedMods.find(m => m._id === row._id);
                  return existing || row;
                });
const newSuggestedServices = suggestedServices.map(row => {
  const stillSelected = selected.some(s => s._id === row._id);
  if (!stillSelected) {
    return { ...row, modifiedQtyByPatient: '', remarks: '' };
  }
  return row;
});
setSuggestedServices(newSuggestedServices);
                modSeqs.current = merged;
                setSelectedRowsState(selected);
              }}
              selectableRowSelected={(row) => selectedRowsState.some(r => r._id === row._id)}
            />
          ) : (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '12px 0' }}>No new services to bill for this consultation.</div>
          )}
        </div>
      </div>

      {/* ── Previously Billed Services (from past consultations) — collapsed by default ── */}
      {previouslyBoughtServices.length > 0 && (
        <div style={{ ...card, opacity: 0.75 }}>
          <div onClick={() => setBilledExpanded(prev => !prev)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            {cardHeader('📋', `Previous Consultations — Already Billed (${previouslyBoughtServices.length})`)}
            <span style={{ marginLeft: 'auto', marginRight: 16, fontSize: 14, color: '#64748b', userSelect: 'none' }}>
              {billedExpanded ? '▲' : '▼'}
            </span>
          </div>
          {billedExpanded && (
            <div style={{ padding: '12px 18px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px', fontWeight: 600, color: '#475569' }}>Service</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600, color: '#475569' }}>Qty</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600, color: '#475569' }}>Amount</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600, color: '#475569' }}>Billed On</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600, color: '#475569' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previouslyBoughtServices.map((s, i) => {
                    const b = s._billing || {};
                    const isPaid = b.balance != null ? Number(b.balance) <= 0 : false;
                    const dateStr = b.invoicedate ? new Date(b.invoicedate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
                    return (
                      <tr key={i} style={{ background: i % 2 ? '#fafbfd' : '#fff', borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '7px 10px', color: '#64748b' }}>{s.name}</td>
                        <td style={{ padding: '7px 10px', color: '#64748b' }}>{s.qty || 1}</td>
                        <td style={{ padding: '7px 10px', color: '#64748b' }}>₹{s.amount || 0}</td>
                        <td style={{ padding: '7px 10px', color: '#64748b' }}>{dateStr}</td>
                        <td style={{ padding: '7px 10px' }}>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: isPaid ? '#dcfce7' : '#fef3c7', color: isPaid ? '#16a34a' : '#d97706' }}>
                            {isPaid ? 'Paid' : 'Unpaid'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Action Button ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 16 }}>
        <button
          type="button"
          onClick={updateSeqs}
          style={{
            padding: '11px 32px',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: '0.02em',
            boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
          }}
        >
          Go To Billing →
        </button>
      </div>
    </div>
  );
};

export default PostConsultation;
